import { useEffect, useState } from "react";

export type ChangelogKind = "major" | "minor" | "patch";

export type ChangelogCategory =
  | "feature"
  | "bug-fix"
  | "security"
  | "polish"
  | "performance"
  | "infrastructure"
  | "refactor"
  | "docs";

export interface ChangelogEntry {
  sha: string;
  ts: string;
  /** Severity tag. Major + minor bump the user-visible version (and
   *  trigger the SW "update available" banner); patch is silent. null
   *  for legacy entries written before the convention landed. */
  kind: ChangelogKind | null;
  /** Pipeline tracker reference (e.g. "MEMSO-#42"). Rendered as a
   *  clickable badge that links to wakilabs.dev/pipeline/<ref_id>.
   *  null when the entry has no associated pipeline item. */
  ref_id: string | null;
  /** Controlled-vocabulary category for the badge color + icon
   *  treatment in the What's New panel. null for legacy entries. */
  category: ChangelogCategory | null;
  summary: string;
  bullets: string[];
}

const STORAGE_KEY = "memso.lastSeenChangelogSha";
const POLL_MS = 60_000;

let cached: ChangelogEntry[] = [];
let cachedSha: string | null = null;
const listeners = new Set<(entries: ChangelogEntry[], lastSeen: string | null) => void>();

function readLastSeen(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setLastSeen(sha: string | null): void {
  try {
    if (sha) localStorage.setItem(STORAGE_KEY, sha);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  cachedSha = sha;
  notify();
}

function notify(): void {
  for (const fn of listeners) {
    try {
      fn(cached, cachedSha);
    } catch {
      /* ignore */
    }
  }
}

async function fetchRecent(): Promise<ChangelogEntry[]> {
  try {
    const res = await fetch("/api/changelog/recent", { credentials: "same-origin" });
    if (!res.ok) return [];
    const body = (await res.json()) as { entries?: ChangelogEntry[] };
    return Array.isArray(body.entries) ? body.entries : [];
  } catch {
    return [];
  }
}

let started = false;

export function startChangelogPoll(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  cachedSha = readLastSeen();
  const tick = async () => {
    const entries = await fetchRecent();
    cached = entries;
    notify();
  };
  void tick();
  window.setInterval(tick, POLL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void tick();
  });
}

export function unseenEntries(entries: ChangelogEntry[], lastSeen: string | null): ChangelogEntry[] {
  if (!entries || entries.length === 0) return [];
  if (!lastSeen) {
    // First-ever visit: don't pop a wall of toasts. Return [] and the
    // caller's first dismiss / View all snaps lastSeen to the newest
    // sha so subsequent pushes only show the diff.
    setLastSeen(entries[0].sha);
    return [];
  }
  const idx = entries.findIndex((e) => e.sha === lastSeen);
  return idx < 0 ? entries : entries.slice(0, idx);
}

export function useChangelog(): { entries: ChangelogEntry[]; lastSeen: string | null; unseen: ChangelogEntry[] } {
  const [state, setState] = useState({ entries: cached, lastSeen: cachedSha });
  useEffect(() => {
    const fn = (entries: ChangelogEntry[], lastSeen: string | null) => {
      setState({ entries, lastSeen });
    };
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  const unseen = unseenEntries(state.entries, state.lastSeen);
  return { entries: state.entries, lastSeen: state.lastSeen, unseen };
}
